// @ts-nocheck
import React, { Suspense, useState, useEffect } from 'react';
import { apiGet } from '../utils/apiClient';
import './DocModeRouter.css';

// Panel registry: maps doc_type to lazy-loaded panel components
const MODE_PANELS: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  academic_paper: React.lazy(() => import('./AnalysisReportPanel')),
  financial_report: React.lazy(() => import('./FinancialReportPanel')),
};

/**
 * PanelSkeleton — loading placeholder while lazy panel loads
 */
function PanelSkeleton() {
  return (
    <div className="doc-mode-skeleton-overlay">
      <div className="doc-mode-skeleton-panel">
        <div className="doc-mode-skeleton-header">
          <div className="skeleton-bar skeleton-title" />
          <div className="skeleton-bar skeleton-subtitle" />
        </div>
        <div className="doc-mode-skeleton-body">
          <div className="skeleton-bar skeleton-line" />
          <div className="skeleton-bar skeleton-line short" />
          <div className="skeleton-bar skeleton-line" />
          <div className="skeleton-bar skeleton-line medium" />
          <div className="skeleton-bar skeleton-line short" />
        </div>
        <div className="doc-mode-skeleton-spinner">
          <div className="spinner" />
          <span>加载分析面板...</span>
        </div>
      </div>
    </div>
  );
}

interface UnknownDocTypePromptProps {
  fingerprint: string;
  onSelect: (docType: string) => void;
  onClose: () => void;
  [key: string]: any;
}

/**
 * UnknownDocTypePrompt — prompt user to select document type
 */
function UnknownDocTypePrompt({ fingerprint, onSelect, onClose }: UnknownDocTypePromptProps) {
  return (
    <div className="doc-mode-unknown-overlay" onClick={onClose}>
      <div className="doc-mode-unknown-card" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="unknown-card-icon">📄</div>
        <h3 className="unknown-card-title">未能自动识别文档类型</h3>
        <p className="unknown-card-desc">
          请选择文档类型以获得最佳分析体验
        </p>
        <div className="unknown-card-options">
          <button
            className="unknown-option-btn academic"
            onClick={() => onSelect('academic_paper')}
            title="Academic Paper"
          >
            📚 学术论文
          </button>
          <button
            className="unknown-option-btn financial"
            onClick={() => onSelect('financial_report')}
            title="Financial Report"
          >
            📊 金融研报
          </button>
        </div>
        <button className="unknown-skip-link" onClick={onClose}>
          跳过
        </button>
      </div>
    </div>
  );
}

interface GenericArtifactViewerProps {
  fingerprint: string;
  onClose: () => void;
  [key: string]: any;
}

/**
 * GenericArtifactViewer — fallback viewer that shows raw artifacts as JSON
 */
function GenericArtifactViewer({ fingerprint, onClose }: GenericArtifactViewerProps) {
  const [artifacts, setArtifacts] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!fingerprint) return;
    setLoading(true);
    setError(null);
    apiGet<Record<string, unknown>>(`/api/research/artifacts/${fingerprint}`)
      .then(data => {
        setArtifacts(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch artifacts:', err);
        setError(err.message || '加载失败');
        setLoading(false);
      });
  }, [fingerprint]);

  const toggleSection = (key: string) => {
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="generic-viewer-overlay" onClick={onClose}>
      <div className="generic-viewer-panel" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="generic-viewer-header">
          <h3>文档分析结果</h3>
          <button className="generic-viewer-close" onClick={onClose}>&times;</button>
        </div>
        <div className="generic-viewer-body">
          {loading && (
            <div className="generic-viewer-loading">
              <div className="spinner" />
              <span>加载中...</span>
            </div>
          )}
          {error && (
            <div className="generic-viewer-error">
              加载失败: {error}
            </div>
          )}
          {!loading && !error && artifacts && (
            <div className="generic-viewer-content">
              {Object.entries(artifacts).map(([key, value]) => (
                <div key={key} className="generic-artifact-section">
                  <button
                    className="generic-artifact-header"
                    onClick={() => toggleSection(key)}
                  >
                    <span className="artifact-collapse-icon">
                      {collapsed[key] ? '▶' : '▼'}
                    </span>
                    <span className="artifact-type-label">{key}</span>
                  </button>
                  {!collapsed[key] && (
                    <pre className="generic-artifact-json">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              {Object.keys(artifacts).length === 0 && (
                <div className="generic-viewer-empty">
                  暂无分析结果
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface DocModeRouterProps {
  docType: string;
  fingerprint: string;
  show: boolean;
  onClose: () => void;
  onDocTypeChange: (docType: string) => void;
  onNavigateToPage?: (page: number) => void;
  [key: string]: any;
}

const DocModeRouter: React.FC<DocModeRouterProps> = ({ docType, fingerprint, show, onClose, onDocTypeChange, onNavigateToPage }) => {
  if (!show) return null;

  const Panel = MODE_PANELS[docType];

  if (!Panel) {
    if (docType === 'unknown') {
      return (
        <UnknownDocTypePrompt
          fingerprint={fingerprint}
          onSelect={onDocTypeChange}
          onClose={onClose}
        />
      );
    }
    return (
      <GenericArtifactViewer
        fingerprint={fingerprint}
        onClose={onClose}
      />
    );
  }

  return (
    <Suspense fallback={<PanelSkeleton />}>
      <Panel
        show={show}
        fingerprint={fingerprint}
        onClose={onClose}
        onNavigateToPage={onNavigateToPage}
      />
    </Suspense>
  );
};

export default DocModeRouter;
